// SEO landing pages — original content written for real travelers.
// Images reuse the app's existing curated Switzerland photography.
// Pages 1-2 also carry German (de-CH) and French (fr-CH) versions.

const IMG = {
  alps: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=srgb&fm=jpg&q=85",
  meadow: "https://images.unsplash.com/photo-1589223050279-18d1f14059cf?crop=entropy&cs=srgb&fm=jpg&q=85",
  lauterbrunnen: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?crop=entropy&cs=srgb&fm=jpg&q=85",
  mountainlake: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?crop=entropy&cs=srgb&fm=jpg&q=85",
  interlaken: "https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?crop=entropy&cs=srgb&fm=jpg&q=85",
  zermatt: "https://images.unsplash.com/photo-1508677019047-f0e50a95656c?crop=entropy&cs=srgb&fm=jpg&q=85",
  lucerne: "https://images.unsplash.com/photo-1560719889-fb75017790a5?crop=entropy&cs=srgb&fm=jpg&q=85",
  zurich: "https://images.unsplash.com/photo-1515488764276-beab7607c1e6?crop=entropy&cs=srgb&fm=jpg&q=85",
  geneva: "https://images.unsplash.com/photo-1633022326182-1b36700bc49a?crop=entropy&cs=srgb&fm=jpg&q=85",
  road: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&q=85",
};

export const SEO_PAGES = [
  {
    slug: "private-tours-switzerland",
    seoTitle: "Private Tours in Switzerland — Tailor-Made with Local Guides | Voyage Switzerland",
    metaDescription:
      "Discover Switzerland on a private tour built around you: expert local guides, flexible itineraries, door-to-door comfort. Alps, lakes and cities — your pace, your way.",
    h1: "Private Tours in Switzerland, Designed Around You",
    eyebrow: "Private Tours",
    image: IMG.alps,
    imageAlt: "Private guided tour group overlooking the Swiss Alps near Lauterbrunnen",
    intro:
      "A private tour means Switzerland bends to your schedule — not the other way around. No bus timetables, no strangers' itineraries: just you, your travel companions and a local expert who knows which meadow the light hits first and which café serves lunch with the best view.",
    sections: [
      {
        h2: "What a Private Tour with Us Looks Like",
        body: "Every tour starts with a conversation. Tell us what you love — panoramic railways, quiet lakes, mountain gastronomy, photography stops, or simply moving slowly — and we shape the day around it. Your guide picks you up at your hotel, adapts the route to the weather and your energy, and stays flexible when you fall in love with a place and want to linger.",
        bullets: [
          "Door-to-door pickup from your hotel, chalet or the airport",
          "Local, English-speaking driver-guides who grew up in these valleys",
          "Flexible timing — start early for sunrise or sleep in, your call",
          "Comfortable vehicles with space for luggage, skis or strollers",
        ],
      },
      {
        h2: "Where Your Private Tour Can Take You",
        body: "Most guests combine one icon with one secret: the Matterhorn and a family-run cheese dairy, Lucerne's Chapel Bridge and a farm lunch above the lake. From the Bernese Oberland to the Engadin, we plan routes that fit real travel days — with time to breathe, photograph and taste.",
      },
      {
        h2: "Who Books Private Tours",
        body: "Couples on honeymoons, families with three generations in tow, photographers chasing alpenglow, and business travelers with one precious free day. If Switzerland is a once-in-a-lifetime trip for you, a private guide makes sure nothing is wasted.",
      },
    ],
    faqs: [
      {
        q: "How far in advance should I book a private tour?",
        a: "For June to September and the Christmas season, two to four weeks ahead is ideal. Outside peak months we can often arrange a private tour with 48 hours' notice.",
      },
      {
        q: "Can we change the itinerary on the day?",
        a: "Yes — that is the point of going private. Your guide adjusts stops, timing and even the region if weather or mood suggests something better.",
      },
      {
        q: "Are private tours suitable for children and seniors?",
        a: "Absolutely. We plan walking distances, rest stops and activities around your group, and provide child seats on request.",
      },
    ],
    relatedTours: ["1", "3", "4"],
    relatedPages: ["luxury-tours-switzerland", "swiss-alps-tours", "switzerland-chauffeur-service"],
  },
  {
    slug: "luxury-tours-switzerland",
    seoTitle: "Luxury Switzerland Tours — Five-Star Journeys, Privately Guided | Voyage Switzerland",
    metaDescription:
      "Luxury Switzerland tours with private guides, premium vehicles, grand-hotel addresses and insider access — from champagne picnics above Lake Geneva to the Glacier Express.",
    h1: "Luxury Tours Through Switzerland's Finest Addresses",
    eyebrow: "Luxury Travel",
    image: IMG.geneva,
    imageAlt: "Lake Geneva with the Jet d'Eau fountain and Mont Blanc, a luxury Switzerland tour highlight",
    intro:
      "Luxury in Switzerland is not loud — it is a suite at a grand hotel that has hosted royalty, a table reserved before the guidebooks arrive, and a guide who knows the mountain concierge by name. We build journeys where every detail is handled before you think to ask.",
    sections: [
      {
        h2: "The Voyage Switzerland Definition of Luxury",
        body: "It begins with time: unhurried mornings, no queues, no waiting on strangers. Add premium vehicles with Wi-Fi and refreshments, reservations at mountain restaurants you cannot walk into, and experiences money alone does not arrange — a private cellar tasting in Lavaux, a chocolate atelier after closing hours, a sunrise platform opened early.",
        bullets: [
          "Five-star and boutique grand hotels, pre-inspected by our team",
          "Premium Mercedes fleet with professional driver-guides",
          "First-class panoramic rail: Glacier Express, Bernina Express, GoldenPass",
          "Insider experiences: wineries, ateliers, private viewings",
        ],
      },
      {
        h2: "Signature Luxury Routes",
        body: "A classic week runs Zurich to St. Moritz via the Bernina line, then on to Zermatt and Lake Geneva — but luxury is personal. Golf at 1,700 metres, a spa afternoon in Leukerbad, or heli-sightseeing over the Eiger: we compose the journey around your version of indulgence.",
      },
      {
        h2: "Discretion as Standard",
        body: "Many of our guests value privacy as much as comfort. Our guides are practiced, discreet hosts — whether you are traveling with family, celebrating something worth celebrating, or simply prefer your holiday to remain yours.",
      },
    ],
    faqs: [
      {
        q: "Can you combine luxury hotels with mountain adventures?",
        a: "That combination is our specialty — a morning on a glacier, an afternoon in a five-star spa. Switzerland's distances are short, so you never trade comfort for scenery.",
      },
      {
        q: "Do you arrange multi-day luxury itineraries?",
        a: "Yes, from three days to three weeks, including hotels, rail, dining reservations and experiences. You receive one itinerary, one contact, one seamless trip.",
      },
      {
        q: "What is the best season for a luxury Switzerland tour?",
        a: "June to September for lakes and alpine dining, December to March for winter glamour in St. Moritz, Gstaad and Zermatt. Each season has its own character — ask us what suits you.",
      },
    ],
    relatedTours: ["6", "3", "2"],
    relatedPages: ["private-tours-switzerland", "st-moritz-private-tours", "gstaad-private-tours"],
  },
  {
    slug: "zurich-private-tours",
    seoTitle: "Zurich Private Tours — Old Town, Lake & Local Secrets | Voyage Switzerland",
    metaDescription:
      "Private tours of Zurich with local guides: the old town, Lake Zurich, Bahnhofstrasse, Uetliberg and day trips to Rhine Falls or Lucerne. Hotel pickup included.",
    h1: "Private Tours of Zurich, Switzerland's Effortless City",
    eyebrow: "Zurich",
    image: IMG.zurich,
    imageAlt: "Zurich old town with the Limmat river and Grossmünster towers on a private city tour",
    intro:
      "Zurich rewards those who slow down: medieval lanes in Niederdorf, espresso on the Lindenhof where Romans once stood, and a lake so clean locals swim at lunchtime. A private tour lets you feel the city's rhythm instead of ticking off landmarks.",
    sections: [
      {
        h2: "Highlights of a Private Zurich Tour",
        body: "We begin where Zurich began — the Lindenhof hill — then wind through the guild houses of the old town to the Grossmünster and Fraumünster with Chagall's glowing windows. Down Bahnhofstrasse to the lake, a promenade at Bürkliplatz, and if you like, up the Uetliberg for the city-meets-Alps panorama.",
        bullets: [
          "Old town, Lindenhof, Grossmünster and Chagall windows",
          "Lake Zurich promenade and optional private boat cruise",
          "Zurich West: viaduct arches, design shops and food halls",
          "Hotel or airport pickup, entirely at your pace",
        ],
      },
      {
        h2: "Easy Day Trips from Zurich",
        body: "Zurich is the perfect base: Rhine Falls in forty minutes, Lucerne in under an hour, the Bernese Oberland by lunch. Many guests book a morning city tour and an afternoon escape — we arrange both as one seamless day.",
      },
    ],
    faqs: [
      {
        q: "How long does a private Zurich tour take?",
        a: "Our classic city tour runs three to four hours. A full-day version adds the lake cruise and Uetliberg, or a day trip to Rhine Falls or Lucerne.",
      },
      {
        q: "Can the tour start at Zurich Airport?",
        a: "Yes — we offer layover tours with airport pickup and guaranteed on-time return. Tell us your flight times when booking.",
      },
      {
        q: "Is Zurich walkable for seniors?",
        a: "The old town has some cobblestones and gentle slopes. We adapt the route, add café stops, and can use the vehicle for the hillier parts.",
      },
    ],
    relatedTours: ["5", "4"],
    relatedPages: ["zurich-airport-transfer", "lucerne-private-tours", "private-tours-switzerland"],
  },
  {
    slug: "geneva-private-tours",
    seoTitle: "Geneva Private Tours — Jet d'Eau, Old Town & Lake Geneva | Voyage Switzerland",
    metaDescription:
      "Private Geneva tours: the old town, Jet d'Eau, United Nations quarter and Lake Geneva shores, with optional Lavaux vineyards or Mont Blanc day trips. Hotel pickup.",
    h1: "Private Tours of Geneva, Switzerland's Cosmopolitan Lakeside",
    eyebrow: "Geneva",
    image: IMG.geneva,
    imageAlt: "Private tour along Lake Geneva with the Jet d'Eau water fountain and alpine backdrop",
    intro:
      "Geneva is where Switzerland meets the world — diplomats at the Palais des Nations, watchmakers in the old town, and a 140-metre fountain that never gets old. With a private guide you see the city behind the institutions: quiet courtyards, lakeside bathing culture, and vineyards minutes away.",
    sections: [
      {
        h2: "What We Show You in Geneva",
        body: "St. Pierre Cathedral and the Reformation Wall, the Flower Clock and the Jet d'Eau, then across the Rhône to the United Nations quarter and the Broken Chair. We finish the Genevan way — a stroll at Bains des Pâquis, where locals swim facing Mont Blanc.",
        bullets: [
          "Old town, cathedral and Reformation Wall with a local historian's eye",
          "Jet d'Eau, Flower Clock and the English Garden",
          "United Nations and international quarter",
          "Optional: Lavaux vineyard terraces (UNESCO) with private tasting",
        ],
      },
      {
        h2: "Geneva as a Gateway",
        body: "Chamonix and Mont Blanc are an hour away; the terraced vineyards of Lavaux even closer. Geneva rewards a second day — tell us your interests and we design the extension.",
      },
    ],
    faqs: [
      {
        q: "Do you guide in French as well as English?",
        a: "Yes — Geneva tours are available in English, French and German as standard, with other languages on request.",
      },
      {
        q: "Can we add a Mont Blanc day trip?",
        a: "Absolutely. Chamonix with the Aiguille du Midi cable car is our most requested Geneva extension — a full day, door to door.",
      },
      {
        q: "Is Geneva good for a half-day tour?",
        a: "Yes. Three hours covers the old town, lakefront and international quarter comfortably. Add Lavaux or the lake cruise for a full day.",
      },
    ],
    relatedTours: ["6"],
    relatedPages: ["geneva-airport-transfer", "luxury-tours-switzerland", "private-tours-switzerland"],
  },
  {
    slug: "lucerne-private-tours",
    seoTitle: "Lucerne Private Tours — Chapel Bridge, Lake & Mountain Days | Voyage Switzerland",
    metaDescription:
      "Private tours of Lucerne: Chapel Bridge, the old town and Lion Monument, Lake Lucerne cruises and mountain excursions to Pilatus or Rigi. Local guides, hotel pickup.",
    h1: "Private Tours of Lucerne, the Heart of Switzerland",
    eyebrow: "Lucerne",
    image: IMG.lucerne,
    imageAlt: "Chapel Bridge and Water Tower in Lucerne visited on a private guided tour",
    intro:
      "Lucerne is the Switzerland of postcards that actually exists: a medieval bridge across a jade-green river, painted facades, and mountains rising straight from the lake. It is compact enough for a gentle morning and rich enough for a week.",
    sections: [
      {
        h2: "A Private Day in Lucerne",
        body: "We cross the Chapel Bridge when the light is soft, walk the Musegg Wall for the rooftop view, and pause at the Lion Monument — Mark Twain called it the most mournful piece of stone in the world. Then the lake: a panorama cruise beneath Pilatus, optionally paired with chocolate tasting or a fondue lunch in the old town.",
        bullets: [
          "Chapel Bridge, Water Tower and the painted old town squares",
          "Lion Monument and the Musegg Wall towers",
          "Lake Lucerne panorama cruise included in our full-day tour",
          "Golden hour from the city wall — our favorite photo stop",
        ],
      },
      {
        h2: "Mountains on the Doorstep",
        body: "Pilatus by cogwheel railway, Rigi by boat and mountain train, Titlis for year-round snow — Lucerne is the gateway to central Switzerland's peaks. A private guide times the connections so you spend the day on summits, not platforms.",
      },
    ],
    faqs: [
      {
        q: "Which mountain excursion do you recommend from Lucerne?",
        a: "Pilatus for drama, Rigi for gentle panoramas, Titlis for guaranteed snow. Tell us the season and your group and we will recommend honestly.",
      },
      {
        q: "Is Lucerne suitable for a day trip from Zurich?",
        a: "Perfectly — under an hour each way. Our Zurich guests often combine a Lucerne morning with an afternoon lake cruise.",
      },
      {
        q: "How much walking is involved?",
        a: "The classic tour covers two to three kilometres at an easy pace. The Musegg Wall has steps; we can skip it or drive up instead.",
      },
    ],
    relatedTours: ["4", "1"],
    relatedPages: ["zurich-private-tours", "swiss-alps-tours", "private-tours-switzerland"],
  },
  {
    slug: "interlaken-private-tours",
    seoTitle: "Interlaken Private Tours — Lakes, Jungfrau & Adventure | Voyage Switzerland",
    metaDescription:
      "Private Interlaken tours between Lake Brienz and Lake Thun: Harder Kulm, Lauterbrunnen valley, Jungfraujoch options and gentle adventure for every traveler.",
    h1: "Private Tours of Interlaken and the Bernese Oberland",
    eyebrow: "Interlaken",
    image: IMG.interlaken,
    imageAlt: "Interlaken between Lake Brienz and Lake Thun with paragliders, seen on a private tour",
    intro:
      "Interlaken sits between two lakes of impossible colors — turquoise Brienz, deep-blue Thun — with the Jungfrau massif on the horizon. It is Switzerland's adventure capital, but with a private guide it can also be its most peaceful corner.",
    sections: [
      {
        h2: "Your Interlaken, Your Tempo",
        body: "Morning coffee on the Höhematte as paragliders land in front of the peaks. A cruise on Lake Brienz to the Giessbach Falls. The Lauterbrunnen valley's 72 waterfalls, or the Harder Kulm funicular for the classic two-lake view. Adventurous? Add paragliding, a canyon swing or an e-bike tour — gentle? We know the lakeside benches with the best views.",
        bullets: [
          "Lake Brienz cruise and Giessbach Falls",
          "Lauterbrunnen valley of 72 waterfalls",
          "Harder Kulm two-lakes viewpoint by funicular",
          "Optional adventure: paragliding, e-bikes, canyon experiences",
        ],
      },
      {
        h2: "Jungfraujoch and Beyond",
        body: "The Top of Europe railway is the region's famous splurge; we arrange tickets and timing to beat the crowds. Equally memorable and quieter: Grindelwald First, Männlichen, or a farm lunch above the valley — we will help you choose.",
      },
    ],
    faqs: [
      {
        q: "Is Jungfraujoch worth it?",
        a: "On a clear day, yes — it is genuinely extraordinary. We check the summit webcams before you commit and always have a beautiful plan B in the valley.",
      },
      {
        q: "Can families with small children enjoy Interlaken?",
        a: "Very much — boat cruises, funiculars and waterfall walks delight children, and every activity can be swapped for a gentler option.",
      },
      {
        q: "How many days should we stay?",
        a: "Two days covers the highlights comfortably; three lets you add a summit or a quieter valley. Our two-day tour is the most booked.",
      },
    ],
    relatedTours: ["2", "1"],
    relatedPages: ["swiss-alps-tours", "lucerne-private-tours", "zurich-airport-transfer"],
  },
  {
    slug: "zermatt-private-tours",
    seoTitle: "Zermatt Private Tours — Matterhorn Views & Car-Free Charm | Voyage Switzerland",
    metaDescription:
      "Private Zermatt tours: Gornergrat railway, Matterhorn viewpoints, the car-free village and glacier experiences. Local guides handle tickets, timing and transfers.",
    h1: "Private Tours of Zermatt, in the Matterhorn's Shadow",
    eyebrow: "Zermatt",
    image: IMG.zermatt,
    imageAlt: "The Matterhorn above Zermatt village, highlight of a private Zermatt tour",
    intro:
      "Some places need no introduction — the Matterhorn is one of them. Zermatt, the car-free village at its feet, has been welcoming travelers since the first alpinists. A private tour handles the logistics (shuttles, tickets, timing) so you simply look up.",
    sections: [
      {
        h2: "The Classic Zermatt Experience",
        body: "We time the Gornergrat cogwheel railway for the clearest morning light — 3,089 metres with the Matterhorn filling the sky. Then the village: 17th-century larch-wood barns in Hinterdorf, the mountaineers' cemetery, and hot chocolate where the first climbers planned their ascents.",
        bullets: [
          "Gornergrat railway with reserved seats and timing advice",
          "Car-free village walk through the old Hinterdorf quarter",
          "Glacier garden and the best Matterhorn photo spots",
          "Sunrise or sunset viewpoint options for photographers",
        ],
      },
      {
        h2: "Getting There Is Half the Pleasure",
        body: "Zermatt has no cars — you arrive by shuttle from Täsch or by the Glacier Express route. We coordinate every leg from your hotel, including luggage, so the car-free charm never becomes a car-free chore.",
      },
    ],
    faqs: [
      {
        q: "When is the Matterhorn most visible?",
        a: "Mornings are usually clearest, and the peak creates its own weather. We build flexibility into every Zermatt tour and check conditions before each departure.",
      },
      {
        q: "Is Zermatt worth visiting in summer?",
        a: "Absolutely — wildflower meadows, glacier views and 400 kilometres of hiking trails. Summer skiing on the glacier is a curiosity many guests love.",
      },
      {
        q: "Will the altitude affect me?",
        a: "Gornergrat is 3,089 m. Most guests feel nothing beyond mild breathlessness; we ascend gradually by train and keep the pace easy.",
      },
    ],
    relatedTours: ["3", "1"],
    relatedPages: ["swiss-alps-tours", "luxury-tours-switzerland", "st-moritz-private-tours"],
  },
  {
    slug: "st-moritz-private-tours",
    seoTitle: "St. Moritz Private Tours — Engadin Lakes & Alpine Glamour | Voyage Switzerland",
    metaDescription:
      "Private St. Moritz tours in the Engadin: lake promenades, Bernina Express, Muottas Muragl panoramas and the resort's legendary style. Privately guided, all seasons.",
    h1: "Private Tours of St. Moritz and the Engadin",
    eyebrow: "St. Moritz",
    image: IMG.mountainlake,
    imageAlt: "Engadin mountain lake near St. Moritz with alpine reflections on a private tour",
    intro:
      "St. Moritz invented the alpine winter holiday in 1864 and has hosted the beautiful crowd ever since — yet its real luxury is the light. The Engadin's high valley sparkles with a clarity painters chased for centuries, and three lakes mirror the peaks.",
    sections: [
      {
        h2: "St. Moritz Beyond the Glamour",
        body: "Yes, there are palace hotels and polo on the frozen lake. But a private tour reveals the quieter Engadin: the leaning tower of a 12th-century church, Segantini's alpine paintings, a walk around Lake St. Moritz as the morning mist lifts, and villages like Sils Maria where Nietzsche spent his summers.",
        bullets: [
          "Lake promenade and the famous 'Champagne climate'",
          "Muottas Muragl for the finest Engadin panorama",
          "Bernina Express over the UNESCO-listed Albula line",
          "Sils Maria and the Upper Engadin lake chain",
        ],
      },
      {
        h2: "Summer or Winter?",
        body: "Winter brings frozen-lake elegance and world-class skiing; summer brings wildflowers, sailing and hiking at 1,800 metres. Both are glorious — the Engadin claims 322 days of sunshine a year, and we have never needed to argue with the number.",
      },
    ],
    faqs: [
      {
        q: "How do we get to St. Moritz?",
        a: "From Zurich it is about three hours by road or the spectacular rail route via Chur. Many guests arrive one way and leave the other — we arrange both.",
      },
      {
        q: "Is the Bernina Express included in tours?",
        a: "We arrange first-class seats and time the crossing for the best light on the Morteratsch glacier — one of Europe's great rail journeys.",
      },
      {
        q: "Is St. Moritz only for luxury travelers?",
        a: "The scenery is free and the hiking is for everyone. We tailor St. Moritz tours to comfortable budgets as happily as to five-star ones.",
      },
    ],
    relatedTours: ["1", "3"],
    relatedPages: ["luxury-tours-switzerland", "gstaad-private-tours", "swiss-alps-tours"],
  },
  {
    slug: "gstaad-private-tours",
    seoTitle: "Gstaad Private Tours — Chalets, Cheese & Quiet Luxury | Voyage Switzerland",
    metaDescription:
      "Private Gstaad tours: the car-free promenade, Saanenland chalets, alpine cheese dairies and the GoldenPass line. Understated Swiss luxury with local guides.",
    h1: "Private Tours of Gstaad, Switzerland's Discreet Hideaway",
    eyebrow: "Gstaad",
    image: IMG.meadow,
    imageAlt: "Alpine meadow with chalets near Gstaad in the Saanenland, visited on a private tour",
    intro:
      "Gstaad is where celebrities come to be left alone — no flashy signage, just weathered chalets, flower-box balconies and the gentle clink of cowbells. The motto here is 'come up, slow down', and a private tour is the perfect way to do both.",
    sections: [
      {
        h2: "The Saanenland at Walking Pace",
        body: "The car-free promenade, chalets that have sheltered the same families for three centuries, and a cheese dairy where you watch alp cheese being made over a wood fire — then taste it with the farmer. This is Switzerland's most authentic valley wearing its elegance lightly.",
        bullets: [
          "Car-free village promenade and historic chalets",
          "Alpine cheese dairy visit with tasting",
          "GoldenPass panoramic railway to Montreux",
          "Glacier 3000 for year-round snow and the peak walk",
        ],
      },
      {
        h2: "A Valley That Rewards Curiosity",
        body: "Nearby Lauenensee is one of Switzerland's prettiest small lakes; the village of Saanen has a church with medieval frescoes; and in winter the valley hosts everything from World Cup skiing to hot-air balloon festivals. We shape the day to the season.",
      },
    ],
    faqs: [
      {
        q: "How do we reach Gstaad?",
        a: "Two hours from Geneva or Bern by road, or on the GoldenPass line from Montreux or Interlaken — one of Switzerland's most scenic rail journeys.",
      },
      {
        q: "Is Gstaad suitable for non-skiers?",
        a: "It is arguably better for them — winter walks, horse-drawn sleighs, spa afternoons and long lunches are the real Gstaad ritual.",
      },
      {
        q: "Can we combine Gstaad with Lake Geneva?",
        a: "Beautifully — the GoldenPass connects Gstaad to Montreux in under two hours, so mountains and Riviera fit in one trip.",
      },
    ],
    relatedTours: ["1", "2"],
    relatedPages: ["luxury-tours-switzerland", "st-moritz-private-tours", "private-tours-switzerland"],
  },
  {
    slug: "swiss-alps-tours",
    seoTitle: "Swiss Alps Tours — Peaks, Passes & Panoramic Railways | Voyage Switzerland",
    metaDescription:
      "Swiss Alps tours with private guides: the Bernese Oberland, Valais and Engadin — panoramic railways, glacier viewpoints, alpine villages and honest local advice.",
    h1: "Swiss Alps Tours Through the Heart of the Mountains",
    eyebrow: "Swiss Alps",
    image: IMG.alps,
    imageAlt: "Swiss Alps panorama with snow-capped peaks above a green valley on a guided Alps tour",
    intro:
      "The Alps cover nearly two-thirds of Switzerland, and no two valleys feel alike. The Bernese Oberland is dramatic, the Valais sun-blessed and mighty, the Engadin luminous and serene. A private Alps tour matches the right mountains to your travel style.",
    sections: [
      {
        h2: "How We Build an Alps Tour",
        body: "We start with your appetite for altitude: gentle lake-and-meadow days, big-name summits like Jungfraujoch and Gornergrat, or high passes and hidden valleys. Then we layer in the experiences locals love — a alpine cheese dairy, a mountain inn lunch, a valley of waterfalls — and time everything around mountain weather.",
        bullets: [
          "Bernese Oberland: Interlaken, Lauterbrunnen, Grindelwald",
          "Valais: Zermatt, the Matterhorn and Aletsch Glacier",
          "Engadin: St. Moritz and the Bernina line",
          "Panoramic railways: Glacier, Bernina and GoldenPass Express",
        ],
      },
      {
        h2: "When to Go",
        body: "June to September for hiking and wildflowers; December to March for snow sports and winter magic; late September for golden larch forests — the Alps' best-kept seasonal secret. Whatever the month, we build a weather-proof plan.",
      },
      {
        h2: "Honest Advice Included",
        body: "Not every famous summit is worth its price on every day. Our guides check webcams and conditions each morning and will tell you plainly when a valley day beats a summit day — your memories matter more than a checklist.",
      },
    ],
    faqs: [
      {
        q: "How many days do we need for the Swiss Alps?",
        a: "Three days gives you one region properly; a week links two or three. Our three-day Alps tour is designed as the perfect first taste.",
      },
      {
        q: "Are Alps tours possible in winter for non-skiers?",
        a: "Yes — winter walks, panoramic trains, thermal baths and village culture make the Alps wonderful without skis.",
      },
      {
        q: "Can we see the Matterhorn and the Jungfrau on one trip?",
        a: "Yes — they are in different regions, roughly three hours apart. A four-to-five-day private tour links them comfortably.",
      },
    ],
    relatedTours: ["1", "3", "2"],
    relatedPages: ["private-tours-switzerland", "zermatt-private-tours", "interlaken-private-tours"],
  },
  {
    slug: "switzerland-chauffeur-service",
    seoTitle: "Switzerland Chauffeur Service — Private Driver-Guides | Voyage Switzerland",
    metaDescription:
      "Chauffeur service in Switzerland with professional driver-guides: intercity transfers, scenic alpine routes and full-day private touring. Fixed prices, hotel pickup.",
    h1: "Chauffeur Service in Switzerland with Driver-Guides",
    eyebrow: "Chauffeur Service",
    image: IMG.road,
    imageAlt: "Comfortable chauffeur-driven journey along a scenic Swiss lakeside road",
    intro:
      "Twenty years of experience have taught us one thing: the drive between sights is part of the holiday, not a gap between them. Our chauffeurs are trained guides — so the road from Zurich to Zermatt becomes a story, not a transfer.",
    sections: [
      {
        h2: "More Than a Driver",
        body: "Every Voyage Switzerland chauffeur is a licensed local guide. Expect restaurant recommendations worth following, photo stops at the unmarked viewpoint, and a scenic detour when the pass road is at its best. Fixed prices agreed in advance — no meters, no surprises.",
        bullets: [
          "Professional, discreet, English-speaking driver-guides",
          "Premium vehicles with Wi-Fi, water and phone chargers",
          "Scenic routing on request — passes, lakes and villages",
          "Hourly hire, full-day hire or multi-day touring",
        ],
      },
      {
        h2: "Popular Chauffeur Routes",
        body: "Zurich to Interlaken via the Brünig Pass, Geneva to Zermatt along the Rhône valley, Zurich to St. Moritz over the Julier — or your own list. Multi-day trips keep the same driver and vehicle throughout, so your luggage and your guide travel with you.",
      },
    ],
    faqs: [
      {
        q: "How is the price calculated?",
        a: "By route or by hour, always agreed in advance as a fixed price — tolls, parking and waiting time included.",
      },
      {
        q: "Can the chauffeur also guide us at the stops?",
        a: "Yes, that is exactly the idea. Our driver-guides accompany you into towns and viewpoints, or give you free time if you prefer — your choice each stop.",
      },
      {
        q: "What vehicles do you use?",
        a: "Modern premium sedans and vans with space for luggage. Child seats and boosters are provided free on request.",
      },
    ],
    relatedTours: ["5", "3"],
    relatedPages: ["zurich-airport-transfer", "geneva-airport-transfer", "luxury-tours-switzerland"],
  },
  {
    slug: "zurich-airport-transfer",
    seoTitle: "Zurich Airport Transfer — Private Pickup, Fixed Price | Voyage Switzerland",
    metaDescription:
      "Private Zurich Airport transfers to Zurich city, Lucerne, Interlaken, Zermatt and St. Moritz. Meet & greet in arrivals, flight tracking, fixed prices, 24/7.",
    h1: "Zurich Airport Transfers, Met with a Smile in Arrivals",
    eyebrow: "Airport Transfer",
    image: IMG.zurich,
    imageAlt: "Zurich Airport private transfer along Lake Zurich into the city center",
    intro:
      "After a long flight, Switzerland should begin the moment you land — not after a taxi queue. Your chauffeur tracks your flight, waits in arrivals with your name, helps with luggage and has you gliding along the lake within minutes.",
    sections: [
      {
        h2: "How Your Transfer Works",
        body: "Book in two minutes, receive instant confirmation with your chauffeur's details. We monitor your flight, so delays cost you nothing and early landings are covered. The price you see is the price you pay — tolls, parking and waiting time included.",
        bullets: [
          "Meet & greet inside arrivals with a name board",
          "Flight tracking — free waiting if you are delayed",
          "Fixed prices to city, mountains or lakeside resorts",
          "Child seats and extra luggage space on request",
        ],
      },
      {
        h2: "Beyond Zurich City",
        body: "Most of our guests head further: Lucerne in 50 minutes, Interlaken in under two hours, Zermatt (Täsch) or St. Moritz in about three. Turn the transfer into a tour — a stop at Rhine Falls or a lakeside lunch costs less than you think.",
      },
    ],
    faqs: [
      {
        q: "What happens if my flight is delayed?",
        a: "We track your flight and adjust automatically — waiting is free for delays, and your chauffeur will be there whenever you land.",
      },
      {
        q: "Can we book a round trip?",
        a: "Yes, and departure transfers are timed with a comfortable buffer for check-in. We know Zurich Airport's rhythms by heart.",
      },
      {
        q: "Do you offer layover tours from the airport?",
        a: "Yes — with five hours or more we can show you Zurich's old town and lake, with guaranteed on-time return for your connection.",
      },
    ],
    relatedTours: ["5", "2"],
    relatedPages: ["zurich-private-tours", "switzerland-chauffeur-service", "geneva-airport-transfer"],
  },
  {
    slug: "geneva-airport-transfer",
    seoTitle: "Geneva Airport Transfer — Private Pickup, Fixed Price | Voyage Switzerland",
    metaDescription:
      "Private Geneva Airport transfers to Geneva city, Lausanne, Montreux, Zermatt, Gstaad and Chamonix. Meet & greet, flight tracking, fixed prices, 24/7 service.",
    h1: "Geneva Airport Transfers to City, Lakes and Alps",
    eyebrow: "Airport Transfer",
    image: IMG.geneva,
    imageAlt: "Private transfer from Geneva Airport along Lake Geneva with alpine views",
    intro:
      "Geneva Airport is the gateway to western Switzerland and the French Alps — and the easiest arrival in Europe when someone is waiting for you. Your chauffeur meets you in arrivals, handles the bags, and chooses the lakeside road when the light is right.",
    sections: [
      {
        h2: "Arrive Without Friction",
        body: "Instant confirmation, flight tracking, free waiting for delays and one fixed price agreed in advance. From the terminal we reach the city in 15 minutes, Lausanne in 40, Montreux in under an hour — and the great ski resorts in two to three hours.",
        bullets: [
          "Meet & greet in arrivals with your name board",
          "Fixed prices — no meters, no surge, no surprises",
          "Direct transfers to Zermatt (Täsch), Gstaad, Verbier and Chamonix",
          "English, French and German speaking chauffeurs",
        ],
      },
      {
        h2: "Make the Transfer a First Tour",
        body: "The road from Geneva to the Alps passes the UNESCO vineyards of Lavaux and the castle of Chillon. Ask us to pause for a tasting or a photo — your holiday can start on the way from the airport.",
      },
    ],
    faqs: [
      {
        q: "Do you serve the French side of Geneva Airport?",
        a: "Yes — we meet guests at both the Swiss and French sectors; just tell us your flight and we handle the rest.",
      },
      {
        q: "Can you transfer us directly to ski resorts?",
        a: "That is a specialty — Verbier, Zermatt (via Täsch), Gstaad and Chamonix with winter-equipped vehicles and room for skis.",
      },
      {
        q: "What is the cancellation policy?",
        a: "Free cancellation up to 24 hours before pickup. Plans change; we are travelers too.",
      },
    ],
    relatedTours: ["6", "1"],
    relatedPages: ["geneva-private-tours", "switzerland-chauffeur-service", "zurich-airport-transfer"],
  },
];

// German (de-CH) and French (fr-CH) versions of the two hub pages.
export const SEO_PAGE_TRANSLATIONS = {
  "private-tours-switzerland": {
    de: {
      seoTitle: "Private Touren in der Schweiz — Massgeschneidert mit lokalen Guides | Voyage Switzerland",
      metaDescription:
        "Entdecken Sie die Schweiz auf einer privaten Tour nach Ihrem Rhythmus: lokale Guides, flexible Routen, Komfort von Tür zu Tür. Alpen, Seen und Städte — ganz persönlich.",
      h1: "Private Touren in der Schweiz, massgeschneidert für Sie",
      eyebrow: "Private Touren",
      intro:
        "Eine private Tour bedeutet: Die Schweiz richtet sich nach Ihrem Zeitplan — nicht umgekehrt. Keine Busfahrpläne, keine fremden Routen: nur Sie, Ihre Begleitung und ein lokaler Experte, der weiss, auf welcher Alp das Licht zuerst fällt.",
      sections: [
        {
          h2: "So sieht eine private Tour mit uns aus",
          body: "Jede Tour beginnt mit einem Gespräch. Sagen Sie uns, was Sie lieben — Panoramabahnen, stille Seen, Berggastronomie oder einfach langsames Reisen — und wir formen den Tag danach. Ihr Guide holt Sie am Hotel ab, passt die Route an Wetter und Laune an und bleibt flexibel, wenn Sie sich an einem Ort verlieben.",
          bullets: [
            "Abholung von Tür zu Tür — Hotel, Chalet oder Flughafen",
            "Lokale, englischsprachige Fahrer-Guides aus diesen Tälern",
            "Flexible Zeiten — Sonnenaufgang oder Ausschlafen, Sie entscheiden",
            "Komfortable Fahrzeuge mit Platz für Gepäck, Ski oder Kinderwagen",
          ],
        },
        {
          h2: "Wohin Ihre private Tour führen kann",
          body: "Die meisten Gäste kombinieren eine Ikone mit einem Geheimtipp: das Matterhorn und eine familiengeführte Käserei, Luzerns Kapellbrücke und ein Bauernlunch über dem See. Von Berner Oberland bis Engadin planen wir Routen, die zu echten Reisetagen passen — mit Zeit zum Atmen, Fotografieren und Geniessen.",
        },
      ],
      faqs: [
        {
          q: "Wie früh sollte ich eine private Tour buchen?",
          a: "Für Juni bis September und die Weihnachtszeit sind zwei bis vier Wochen ideal. Ausserhalb der Hochsaison organisieren wir oft mit 48 Stunden Vorlauf.",
        },
        {
          q: "Können wir die Route am Tag selbst ändern?",
          a: "Ja — genau darum geht es bei einer privaten Tour. Ihr Guide passt Stopps, Zeiten und sogar die Region an, wenn Wetter oder Stimmung etwas Besseres nahelegen.",
        },
        {
          q: "Sind private Touren für Kinder und Senioren geeignet?",
          a: "Unbedingt. Wir planen Gehstrecken, Pausen und Aktivitäten passend zu Ihrer Gruppe — Kindersitze auf Wunsch inklusive.",
        },
      ],
    },
    fr: {
      seoTitle: "Circuits privés en Suisse — Sur mesure avec guides locaux | Voyage Switzerland",
      metaDescription:
        "Découvrez la Suisse lors d'un circuit privé à votre rythme : guides locaux, itinéraires flexibles, confort porte-à-porte. Alpes, lacs et villes — rien que pour vous.",
      h1: "Circuits privés en Suisse, conçus autour de vous",
      eyebrow: "Circuits privés",
      intro:
        "Un circuit privé, c'est la Suisse qui s'adapte à votre emploi du temps — et non l'inverse. Pas d'horaires de bus, pas d'itinéraire imposé : juste vous, vos proches et un expert local qui sait quel alpage reçoit la première lumière.",
      sections: [
        {
          h2: "À quoi ressemble un circuit privé avec nous",
          body: "Chaque circuit commence par une conversation. Dites-nous ce que vous aimez — trains panoramiques, lacs paisibles, gastronomie de montagne ou simplement prendre le temps — et nous construisons la journée autour. Votre guide vient vous chercher à l'hôtel, adapte l'itinéraire à la météo et à vos envies, et reste flexible quand un lieu vous retient.",
          bullets: [
            "Prise en charge porte-à-porte — hôtel, chalet ou aéroport",
            "Guides-chauffeurs locaux, anglophones, nés dans ces vallées",
            "Horaires flexibles — lever de soleil ou grasse matinée, à vous de choisir",
            "Véhicules confortables avec place pour bagages, skis ou poussettes",
          ],
        },
        {
          h2: "Où votre circuit privé peut vous mener",
          body: "La plupart de nos clients combinent une icône et un secret : le Cervin et une fromagerie familiale, le Pont de la Chapelle à Lucerne et un déjeuner à la ferme au-dessus du lac. De l'Oberland bernois à l'Engadine, nous traçons des itinéraires faits pour de vraies journées de voyage — avec le temps de respirer, photographier et goûter.",
        },
      ],
      faqs: [
        {
          q: "Combien de temps à l'avance faut-il réserver ?",
          a: "De juin à septembre et pendant les fêtes, deux à quatre semaines sont idéales. Hors saison, nous organisons souvent un circuit avec 48 heures de préavis.",
        },
        {
          q: "Peut-on modifier l'itinéraire le jour même ?",
          a: "Oui — c'est tout l'intérêt du privé. Votre guide ajuste les arrêts, les horaires et même la région si la météo ou l'envie suggère mieux.",
        },
        {
          q: "Les circuits privés conviennent-ils aux enfants et aux seniors ?",
          a: "Absolument. Nous adaptons distances, pauses et activités à votre groupe, et fournissons des sièges enfants sur demande.",
        },
      ],
    },
    ar: {
      seoTitle: "جولات خاصة في سويسرا — مصممة خصيصًا مع مرشدين محليين | Voyage Switzerland",
      metaDescription:
        "اكتشف سويسرا في جولة خاصة على إيقاعك: مرشدون محليون خبراء، مسارات مرنة، وراحة من الباب إلى الباب. جبال وبحيرات ومدن — على طريقتك.",
      h1: "جولات خاصة في سويسرا، مصممة من أجلك",
      eyebrow: "جولات خاصة",
      intro:
        "الجولة الخاصة تعني أن سويسرا تتكيف مع جدولك — لا العكس. لا مواعيد حافلات ولا مسارات مفروضة: أنت ومرافقوك فقط وخبير محلي يعرف أي مرجٍ تلامسه الشمس أولاً وأي مقهى يقدم الغداء بأجمل إطلالة.",
      sections: [
        {
          h2: "كيف تبدو الجولة الخاصة معنا",
          body: "تبدأ كل جولة بحديث. أخبرنا بما تحب — القطارات البانورامية، البحيرات الهادئة، مأكولات الجبال، محطات التصوير، أو ببساطة السفر ببطء — ونشكّل اليوم حوله. يستقبلك مرشدك من فندقك، ويكيّف المسار مع الطقس وطاقتك، ويبقى مرنًا حين تقع في حب مكانٍ ما وتريد إطالة المكوث.",
          bullets: [
            "استقبال من الباب إلى الباب — فندقك أو شاليهك أو المطار",
            "مرشدون وسائقون محليون نشأوا في هذه الوديان",
            "مواعيد مرنة — انطلق مبكرًا لشروق الشمس أو نم حتى متأخر، القرار لك",
            "مركبات مريحة بمساحة للأمتعة أو معدات التزلج أو عربات الأطفال",
          ],
        },
        {
          h2: "إلى أين يمكن أن تأخذك جولتك الخاصة",
          body: "يجمع معظم ضيوفنا بين أيقونة وسرّ: ماترهورن ومصنع أجبان عائلي، جسر الكنيسة في لوتسيرن وغداء ريفي فوق البحيرة. من أوبرلاند برن إلى إنغادين، نخطط مسارات تناسب أيام السفر الحقيقية — مع وقت للتنفس والتصوير والتذوق.",
        },
      ],
      faqs: [
        {
          q: "كم يجب أن أحجز مسبقًا جولة خاصة؟",
          a: "من يونيو إلى سبتمبر وفي موسم الأعياد، يُفضّل الحجز قبل أسبوعين إلى أربعة. خارج موسم الذروة، نرتب الجولة غالبًا بإشعار قبل ٤٨ ساعة فقط.",
        },
        {
          q: "هل يمكن تغيير المسار في اليوم نفسه؟",
          a: "نعم — هذا هو جوهر الجولة الخاصة. يعدّل مرشدك المحطات والتوقيت وحتى المنطقة إذا اقترح الطقس أو المزاج خيارًا أفضل.",
        },
        {
          q: "هل الجولات الخاصة مناسبة للأطفال وكبار السن؟",
          a: "بالتأكيد. نخطط مسافات المشي ومحطات الراحة والأنشطة بما يناسب مجموعتك، ونوفر مقاعد أطفال عند الطلب.",
        },
      ],
    },
  },
  "luxury-tours-switzerland": {
    de: {
      seoTitle: "Luxus-Touren Schweiz — Fünf-Sterne-Reisen, privat geführt | Voyage Switzerland",
      metaDescription:
        "Luxus-Touren in der Schweiz mit privaten Guides, Premium-Fahrzeugen, Grand-Hotels und Insider-Zugang — vom Champagner-Picknick über dem Genfersee bis zum Glacier Express.",
      h1: "Luxus-Touren durch die feinsten Adressen der Schweiz",
      eyebrow: "Luxusreisen",
      intro:
        "Luxus ist in der Schweiz leise — eine Suite in einem Grand Hotel mit königlicher Geschichte, ein reservierter Tisch, bevor die Reiseführer kommen, und ein Guide, der den Berg-Concierge beim Namen kennt. Wir bauen Reisen, bei denen jedes Detail erledigt ist, bevor Sie daran denken.",
      sections: [
        {
          h2: "Unsere Definition von Luxus",
          body: "Es beginnt mit Zeit: ruhige Morgen, keine Warteschlangen, kein Warten auf Fremde. Dazu Premium-Fahrzeuge mit WLAN und Erfrischungen, Reservationen in Bergrestaurants, in die man nicht einfach hineingeht, und Erlebnisse, die Geld allein nicht ermöglicht — eine private Kellerverkostung im Lavaux, ein Schokoladen-Atelier nach Ladenschluss.",
          bullets: [
            "Fünf-Sterne- und Boutique-Grand-Hotels, von uns geprüft",
            "Premium-Flotte mit professionellen Fahrer-Guides",
            "Erste Klasse Panoramabahn: Glacier, Bernina, GoldenPass Express",
            "Insider-Erlebnisse: Weingüter, Ateliers, private Führungen",
          ],
        },
        {
          h2: "Diskretion als Standard",
          body: "Viele unserer Gäste schätzen Privatsphäre ebenso sehr wie Komfort. Unsere Guides sind erfahrene, diskrete Gastgeber — ob Sie mit der Familie reisen, etwas Besonderes feiern oder Ihre Ferien einfach für sich behalten möchten.",
        },
      ],
      faqs: [
        {
          q: "Lassen sich Luxushotels und Bergabenteuer kombinieren?",
          a: "Diese Kombination ist unsere Spezialität — morgens Gletscher, nachmittags Fünf-Sterne-Spa. Die Distanzen in der Schweiz sind kurz, Sie tauschen nie Komfort gegen Landschaft.",
        },
        {
          q: "Organisieren Sie mehrtägige Luxusreisen?",
          a: "Ja, von drei Tagen bis drei Wochen — inklusive Hotels, Bahn, Tischreservationen und Erlebnissen. Eine Reiseroute, ein Kontakt, eine nahtlose Reise.",
        },
        {
          q: "Welche ist die beste Saison für eine Luxusreise?",
          a: "Juni bis September für Seen und Alpenküche, Dezember bis März für Winterglamour in St. Moritz, Gstaad und Zermatt. Fragen Sie uns — wir beraten ehrlich.",
        },
      ],
    },
    fr: {
      seoTitle: "Circuits de luxe en Suisse — Voyages cinq étoiles, guides privés | Voyage Switzerland",
      metaDescription:
        "Circuits de luxe en Suisse avec guides privés, véhicules premium, grands hôtels et accès privilégiés — du pique-nique champagne au-dessus du Léman au Glacier Express.",
      h1: "Circuits de luxe au cœur des plus belles adresses suisses",
      eyebrow: "Voyages de luxe",
      intro:
        "En Suisse, le luxe ne s'affiche pas — c'est une suite dans un palace ayant accueilli des têtes couronnées, une table réservée avant les guides de voyage, et un accompagnateur qui connaît le concierge du sommet par son prénom. Nous créons des voyages où chaque détail est réglé avant même que vous y pensiez.",
      sections: [
        {
          h2: "Notre définition du luxe",
          body: "Tout commence par le temps : des matinées sans hâte, aucune file d'attente. Ajoutez des véhicules premium avec Wi-Fi et rafraîchissements, des réservations dans des restaurants d'altitude inaccessibles sans relations, et des expériences que l'argent seul n'achète pas — dégustation privée en cave à Lavaux, atelier chocolat après la fermeture.",
          bullets: [
            "Palaces et hôtels de charme cinq étoiles, inspectés par nos soins",
            "Flotte premium avec guides-chauffeurs professionnels",
            "Première classe panoramique : Glacier, Bernina et GoldenPass Express",
            "Expériences d'initiés : domaines viticoles, ateliers, visites privées",
          ],
        },
        {
          h2: "La discrétion en standard",
          body: "Nombre de nos clients apprécient la confidentialité autant que le confort. Nos guides sont des hôtes expérimentés et discrets — que vous voyagiez en famille, célébriez quelque chose d'unique ou souhaitiez simplement garder vos vacances pour vous.",
        },
      ],
      faqs: [
        {
          q: "Peut-on combiner hôtels de luxe et aventures alpines ?",
          a: "C'est notre spécialité — le matin sur un glacier, l'après-midi au spa cinq étoiles. Les distances suisses sont courtes : jamais de compromis entre confort et paysages.",
        },
        {
          q: "Organisez-vous des itinéraires de plusieurs jours ?",
          a: "Oui, de trois jours à trois semaines, hôtels, trains, tables et expériences compris. Un seul itinéraire, un seul contact, un voyage sans couture.",
        },
        {
          q: "Quelle est la meilleure saison pour un voyage de luxe ?",
          a: "Juin à septembre pour les lacs et la gastronomie alpine, décembre à mars pour l'élégance hivernale de St. Moritz, Gstaad et Zermatt. Demandez-nous — nous conseillons en toute honnêteté.",
        },
      ],
    },
    ar: {
      seoTitle: "جولات فاخرة في سويسرا — رحلات خمس نجوم بمرافقة خاصة | Voyage Switzerland",
      metaDescription:
        "جولات فاخرة في سويسرا مع مرشدين خاصين ومركبات راقية وفنادق عريقة ووصول حصري — من نزهة شمبانيا فوق بحيرة جنيف إلى قطار Glacier Express.",
      h1: "جولات فاخرة عبر أرقى عناوين سويسرا",
      eyebrow: "سفر فاخر",
      intro:
        "الفخامة في سويسرا لا تصخب — جناح في فندق عريق استضاف الملوك، طاولة محجوزة قبل وصول الأدلة السياحية، ومرشد يعرف كونسيرج الجبل باسمه. نبني رحلات يُنجَز فيها كل تفصيل قبل أن يخطر ببالك.",
      sections: [
        {
          h2: "تعريفنا للفخامة",
          body: "يبدأ الأمر بالوقت: صباحات هادئة، بلا طوابير ولا انتظار. أضف مركبات راقية مع واي فاي ومرطبات، وحجوزات في مطاعم جبلية لا يُدخل إليها بسهولة، وتجارب لا يشتريها المال وحده — تذوق خاص في أقبية لافو، أو مشغل شوكولاتة بعد الإغلاق.",
          bullets: [
            "فنادق خمس نجوم وفنادق بوتيك عريقة، فحصها فريقنا مسبقًا",
            "أسطول مركبات راقٍ مع مرشدين وسائقين محترفين",
            "درجة أولى في القطارات البانورامية: Glacier وBernina وGoldenPass",
            "تجارب حصرية: معامل نبيذ ومشاغل ومشاهدات خاصة",
          ],
        },
        {
          h2: "الخصوصية معيارنا",
          body: "كثير من ضيوفنا يقدّرون الخصوصية بقدر الراحة. مرشدونا مضيفون متمرسون وكتومون — سواء سافرت مع عائلتك، أو احتفلت بمناسبة تستحق، أو فضّلت ببساطة أن تبقى عطلتك لك وحدك.",
        },
      ],
      faqs: [
        {
          q: "هل يمكن الجمع بين الفنادق الفاخرة ومغامرات الجبال؟",
          a: "هذا تخصصنا — صباح على نهر جليدي ومساء في سبا خمس نجوم. المسافات في سويسرا قصيرة، فلا تبدّل الراحة بالمناظر أبدًا.",
        },
        {
          q: "هل تنظمون برامج فاخرة متعددة الأيام؟",
          a: "نعم، من ثلاثة أيام إلى ثلاثة أسابيع، شاملة الفنادق والقطارات وحجوزات المطاعم والتجارب. برنامج واحد، جهة اتصال واحدة، رحلة سلسة.",
        },
        {
          q: "ما أفضل موسم لجولة فاخرة في سويسرا؟",
          a: "من يونيو إلى سبتمبر للبحيرات ومطاعم الألب، ومن ديسمبر إلى مارس لبريق الشتاء في سان موريتز وغشتاد وزيرمات. لكل موسم شخصيته — اسألنا عما يناسبك.",
        },
      ],
    },
  },
};

export const TRANSLATED_SLUGS = Object.keys(SEO_PAGE_TRANSLATIONS);

export const getSeoPage = (slug, lang = "en") => {
  const page = SEO_PAGES.find((p) => p.slug === slug);
  if (!page) return null;
  const t = lang !== "en" ? SEO_PAGE_TRANSLATIONS[slug]?.[lang] : null;
  return t ? { ...page, ...t, faqs: t.faqs } : page;
};
